import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Sparkles, Mail, Lock, User as UserIcon, ArrowRight, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/use-auth";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const search = z.object({ redirect: z.string().optional() });

export const Route = createFileRoute("/auth")({
  validateSearch: search,
  head: () => ({ meta: [{ title: "Sign in — Jul's Skin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AuthPage,
});

function AuthPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: (redirect as any) ?? "/account", replace: true });
  }, [user, loading]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const signIn = await supabase.auth.signInWithPassword({ email, password });
      if (!signIn.error) {
        toast.success("Welcome back");
        return;
      }
      const msg = signIn.error.message?.toLowerCase() ?? "";
      const looksMissing =
        msg.includes("invalid login credentials") ||
        msg.includes("invalid_credentials") ||
        msg.includes("user not found");
      if (!looksMissing) throw signIn.error;

      const signUp = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { full_name: name || email.split("@")[0] },
        },
      });
      if (signUp.error) throw signUp.error;

      if (!signUp.data.session) {
        const retry = await supabase.auth.signInWithPassword({ email, password });
        if (retry.error) {
          toast.success("Account created. Please check your email to verify.");
          return;
        }
      }
      toast.success("Account created — you're signed in");
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  const signInWithGoogle = async () => {
    setSubmitting(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) throw result.error;
    } catch (err: any) {
      toast.error(err.message ?? "Google sign-in failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />

      <div className="relative flex-1 flex items-start justify-center overflow-hidden">
        {/* Ambient background */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(900px 420px at 12% -10%, color-mix(in oklab, var(--primary) 18%, transparent), transparent 60%), radial-gradient(700px 360px at 100% 0%, color-mix(in oklab, var(--primary) 10%, transparent), transparent 65%), linear-gradient(180deg, var(--background), var(--background))",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-[0.035]"
          style={{
            backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="w-full max-w-md px-5 md:px-6 py-10 md:py-16">
          {/* Badge */}
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 backdrop-blur px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-primary">
              <Sparkles className="h-3 w-3" /> Members Club
            </span>
          </div>

          {/* Heading */}
          <p className="eyebrow text-muted-foreground">Welcome</p>
          <h1 className="display text-3xl md:text-5xl mt-2 leading-[1.05]">
            Sign in or <br className="hidden md:block" />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(120deg, var(--primary), color-mix(in oklab, var(--primary) 55%, white))",
              }}
            >
              create account
            </span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            Enter your email and a password. We'll sign you in if the account
            exists, or create it automatically.
          </p>

          {/* Premium card */}
          <form
            onSubmit={submit}
            className="relative mt-8 rounded-3xl border border-border bg-card/80 backdrop-blur p-6 md:p-7 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)]"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-3xl opacity-60"
              style={{
                background:
                  "linear-gradient(140deg, transparent 55%, color-mix(in oklab, var(--primary) 8%, transparent))",
              }}
            />

            <div className="relative space-y-5">
              {/* Google sign-in hidden */}


              <Field
                label="Name (optional)"
                icon={<UserIcon className="h-4 w-4" />}
              >
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-transparent pl-10 pr-3 py-3 text-sm focus:outline-none placeholder:text-muted-foreground/60"
                />
              </Field>

              <Field label="Email" icon={<Mail className="h-4 w-4" />}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-transparent pl-10 pr-3 py-3 text-sm focus:outline-none placeholder:text-muted-foreground/60"
                />
              </Field>

              <Field label="Password" icon={<Lock className="h-4 w-4" />}>
                <input
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-transparent pl-10 pr-10 py-3 text-sm focus:outline-none placeholder:text-muted-foreground/60"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>

              <button
                disabled={submitting}
                className="group relative w-full overflow-hidden rounded-2xl py-4 text-sm font-medium tracking-wide text-primary-foreground transition-all disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(135deg, var(--primary), color-mix(in oklab, var(--primary) 60%, white))",
                  boxShadow:
                    "0 18px 40px -16px color-mix(in oklab, var(--primary) 55%, transparent)",
                }}
              >
                <span className="relative inline-flex items-center justify-center gap-2">
                  {submitting ? "Please wait…" : "Continue"}
                  {!submitting && (
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  )}
                </span>
              </button>

              <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
                By continuing you agree to our{" "}
                <span className="underline underline-offset-2">Terms</span> and{" "}
                <span className="underline underline-offset-2">Privacy Policy</span>.
              </p>
            </div>
          </form>

          <p className="text-center text-[11px] tracking-[0.22em] uppercase text-muted-foreground pt-6">
            Jul,s Skin · Members
          </p>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <div className="relative rounded-2xl border border-border bg-background/60 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/15 transition-all">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        {children}
      </div>
    </label>
  );
}
